import React, { useState, useEffect } from "react";
import { fetchGovernanceHeatmap, fetchResearchFeed } from "../../../lib/researchApi";
import { governanceColor } from "../shared/GovernanceBadge";

const GOVERNANCE_STATES = ["approved", "pilot", "experimental", "preview_only", "degraded", "disabled"];
const STATE_LABELS = {
    approved:     "Approved",
    pilot:        "Pilot",
    experimental: "Experimental",
    preview_only: "Preview Only",
    degraded:     "Degraded",
    disabled:     "Disabled",
};

const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

const EVENT_TYPE_LABELS = {
    governance_transition: "Governance Change",
    validation_completed:  "Validation Run",
    monitoring_alert:      "Alert",
    degradation_event:     "Degradation",
};

function HeatmapCell({ count, maxCount, state }) {
    const intensity = maxCount > 0 ? count / maxCount : 0;
    const base = governanceColor(state);
    return (
        <div style={{
            width: "100%",
            aspectRatio: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: count > 0 ? `${base}${Math.round(intensity * 180 + 30).toString(16).padStart(2, "0")}` : "rgba(255,255,255,0.04)",
            borderRadius: 4,
            fontSize: "14px",
            fontWeight: count > 0 ? 700 : 400,
            color: count > 0 ? base : "var(--savvy-dim)",
            border: count > 0 ? `1px solid ${base}44` : "1px solid rgba(255,255,255,0.06)",
            transition: "background 0.2s",
        }}>
            {count}
        </div>
    );
}

export default function GovernanceSection() {
    const [heatmap, setHeatmap] = useState(null);
    const [feed, setFeed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchGovernanceHeatmap(), fetchResearchFeed(30, 60)])
            .then(([hm, fd]) => {
                setHeatmap(hm.strategies || {});
                setFeed(fd.events || []);
            })
            .catch((e) => setError(e?.response?.data?.error || "Failed to load governance data"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ color: "var(--savvy-muted)", padding: 24, textAlign: "center", fontSize: "13px" }}>Loading governance data…</div>;
    if (error) return <div style={{ color: "#f87171", padding: 16, fontSize: "13px" }}>Error: {error}</div>;

    const strategies = Object.keys(heatmap || {});
    const maxCount = strategies.reduce((max, s) =>
        Math.max(max, ...GOVERNANCE_STATES.map(state => heatmap[s]?.[state] || 0)), 0
    );

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24 }}>
            {/* Heatmap */}
            <div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--savvy-muted)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Governance State Distribution
                </div>
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "6px" }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: "left", fontSize: "11px", color: "var(--savvy-muted)", fontWeight: 500, paddingBottom: 6 }}>
                                    Strategy
                                </th>
                                {GOVERNANCE_STATES.map(state => (
                                    <th key={state} style={{
                                        textAlign: "center",
                                        fontSize: "10px",
                                        color: governanceColor(state),
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                        paddingBottom: 6,
                                        whiteSpace: "nowrap",
                                    }}>
                                        {STATE_LABELS[state]}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {strategies.map(strategy => (
                                <tr key={strategy}>
                                    <td style={{ fontSize: "12px", color: "var(--savvy-text)", fontWeight: 500, paddingRight: 12, whiteSpace: "nowrap" }}>
                                        {STRATEGY_LABELS[strategy] || strategy}
                                    </td>
                                    {GOVERNANCE_STATES.map(state => (
                                        <td key={state} style={{ width: 52, height: 52 }}>
                                            <HeatmapCell
                                                count={heatmap[strategy]?.[state] || 0}
                                                maxCount={maxCount}
                                                state={state}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Current state summary */}
                <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: "11px", color: "var(--savvy-muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Current State
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {GOVERNANCE_STATES.map(state => {
                            const total = strategies.reduce((sum, s) => sum + (heatmap[s]?.[state] || 0), 0);
                            if (total === 0) return null;
                            return (
                                <div key={state} style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 10px",
                                    background: `${governanceColor(state)}1a`,
                                    border: `1px solid ${governanceColor(state)}33`,
                                    borderRadius: 6,
                                }}>
                                    <span style={{ fontSize: "14px", fontWeight: 700, color: governanceColor(state) }}>{total}</span>
                                    <span style={{ fontSize: "10px", color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                        {STATE_LABELS[state]}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Research feed */}
            <div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--savvy-muted)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Recent Events
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {feed.slice(0, 12).map((event, i) => {
                        const ts = event.timestamp ? new Date(event.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
                        const isGovernance = event.event_type === "governance_transition";
                        const isDegradation = event.event_type === "degradation_event";
                        return (
                            <div key={i} style={{
                                padding: "8px 12px",
                                background: "var(--savvy-panel)",
                                border: isDegradation
                                    ? "1px solid rgba(220,38,38,0.3)"
                                    : isGovernance
                                        ? "1px solid rgba(159,234,232,0.15)"
                                        : "1px solid var(--savvy-border)",
                                borderRadius: 6,
                            }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                    <span style={{
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        color: isDegradation ? "#f87171" : isGovernance ? "var(--savvy-accent)" : "var(--savvy-muted)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                    }}>
                                        {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                                    </span>
                                    <span style={{ fontSize: "10px", color: "var(--savvy-dim)" }}>{ts}</span>
                                </div>
                                <div style={{ fontSize: "12px", color: "var(--savvy-text)" }}>
                                    {STRATEGY_LABELS[event.strategy] || event.strategy}
                                    {event.from_state && event.to_state
                                        ? <span style={{ color: "var(--savvy-muted)" }}> · {event.from_state} → {event.to_state}</span>
                                        : null}
                                </div>
                                {event.message && (
                                    <div style={{ fontSize: "11px", color: "var(--savvy-muted)", marginTop: 2 }}>{event.message}</div>
                                )}
                            </div>
                        );
                    })}
                    {feed.length === 0 && (
                        <div style={{ color: "var(--savvy-dim)", fontSize: "12px", padding: "12px 0" }}>No recent events</div>
                    )}
                </div>
            </div>
        </div>
    );
}
